import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Button from '../Button/Button';
import Input from '../Input/Input';
import createFakeEvent from '../../lib/createFakeEvent';
import isValidUri from '../../lib/validateUri';

const ANIMATION_DELAY = 150;

/**
 * `UploadButton` is a button like component which turns into an {@link Input} field and an **Upload** button when it is
 * clicked and can be used to initiate upload of URI based resources.
 *
 * The *onUpload* callback will be called when the user hits the Enter key or clicks on the **Upload** button. The input
 * field will be closed if the user hits the Escape key or clicks outside the input field.
 *
 * All props not listed here will be forwarded to the {@link Input} component.
 *
 * @property {node} children The content of your button. Supports any HTML markup. Defaults to the text "Upload from URI".
 * @property {string} className The class name of this component. Will be appended to "core-upload-button". Supports the
 * "primary" and "tertiary" classes like the {@link Button} component.
 * @property {node} icon An SVG icon to show inside the button. Defaults to no icon as the default icon is set using
 * *iconName*.
 * @property {string} iconName The name of any icon in the **vimond-core-component**'s icon library. See {@link Icon}.
 * Defaults to "upload".
 * @property {function} onUpload A callback that is called when the user has either clicked on the **Upload** button or
 * hit the Enter key in the input field.
 * @property {string} placeholder The placeholder text for the input field. Defaults to "https://".
 * @property {function} validate A validation function that is invoked every time the user makes changes to the input
 * value. Defaults to an internal URI validation function.
 *
 * @see Input
 */
export default class UploadButton extends PureComponent {

  static displayName = 'UploadButton';

  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    icon: PropTypes.node,
    iconName: PropTypes.string,
    onUpload: PropTypes.func,
    placeholder: PropTypes.string,
    validate: PropTypes.func,
  };

  static defaultProps = {
    children: 'Upload from URI',
    iconName: 'upload',
    placeholder: 'https://',
    validate: isValidUri,
  };

  state = {};

  onBlur = e => setTimeout(() => {
    this.setState({ focus: false });
  }, ANIMATION_DELAY);

  onChange = e => this.setState({
    value: e.target.value,
    valid: this.props.validate(e.target.value.trim()),
  });

  onClick = () => {
    if (!this.props.disabled) {
      this.setState({ focus: true, value: '', valid: false });
      setTimeout(() => {
        this.refInput.focus();
      }, ANIMATION_DELAY);
    }
  };

  onKeyDown = e => {
    if (e.keyCode === 13 || e.keyCode === 27) {
      e.preventDefault();
      e.stopPropagation();
      if (e.keyCode === 27) {
        this.refInput.blur();
      } else if (this.state.valid) {
        this.onUpload(e);
      }
    }
  };

  onUpload = e => {
    let value = this.state.value.trim();

    if (value.indexOf('http://') === -1 && value.indexOf('https://') === -1) {
      value = `https://${value}`;
    }
    if (this.props.onUpload) {
      this.props.onUpload(createFakeEvent(value, e, this.refInput.getDOMNode().dataset));
    }
    this.refInput.blur();
  };

  render() {
    const { children, className, disabled, icon, iconName, onUpload, validate, ...rest } = this.props;
    const { focus, valid, value } = this.state;

    return (
      <div className={cx('core-upload-button', className, { focus, disabled })}>
        <div className="core-upload-button-content">
          <div className="normal" onClick={this.onClick}>{children}</div>
          <div className="active">
            <Input value={value} onChange={this.onChange} onBlur={this.onBlur} ref={c => { this.refInput = c; }}
               onKeyDown={this.onKeyDown} disabled={!focus} {...rest}/>
            <Button className="upload" icon={icon} iconName={iconName} onClick={this.onUpload} disabled={!valid}/>
          </div>
        </div>
      </div>
    );
  }

}
